package interviewprep.client

import interviewprep.common.ClientContext
import interviewprep.common.ClientEvent
import interviewprep.common.ClientProtocolWire
import interviewprep.common.InputMode
import interviewprep.common.PracticeMode
import interviewprep.common.SessionSnapshotV1
import interviewprep.common.SessionStartRequest
import interviewprep.common.SessionStartResponseV3
import interviewprep.common.TurnCommandV3
import interviewprep.common.TurnEnvelopeV3
import interviewprep.common.TurnPayload
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import org.w3c.fetch.RequestInit

internal suspend fun startWebSession(
    userId: String,
    track: String,
    practiceMode: PracticeMode
): SessionStartResponseV3 {
    val request = SessionStartRequest(
        userId = userId,
        track = track,
        practiceMode = practiceMode,
        tzOffsetMinutes = timezoneOffsetMinutes(),
        deviceId = userId,
        deviceModel = "web-browser",
        appVersion = "web",
        appBuildNumber = "web"
    )
    val response = window.fetch(
        "/api/v1/web/session/start",
        RequestInit(
            method = "POST",
            headers = jsonHeaders(),
            body = webJson.encodeToString(request)
        )
    ).await()
    return response.decodeOrThrow()
}

internal suspend fun sendTurn(
    sessionId: String,
    snapshot: SessionSnapshotV1,
    answerText: String,
    inputMode: InputMode
): TurnEnvelopeV3 =
    sendTurnCommand(
        sessionId = sessionId,
        snapshot = snapshot,
        event = ClientEvent.ANSWER_SUBMIT,
        payload = TurnPayload(answerText = answerText, answerLanguage = "en"),
        inputMode = inputMode
    )

internal suspend fun sendStartTurn(
    sessionId: String,
    snapshot: SessionSnapshotV1,
    inputMode: InputMode
): TurnEnvelopeV3 =
    sendTurnCommand(
        sessionId = sessionId,
        snapshot = snapshot,
        event = ClientEvent.START,
        payload = null,
        inputMode = inputMode
    )

private suspend fun sendTurnCommand(
    sessionId: String,
    snapshot: SessionSnapshotV1,
    event: ClientEvent,
    payload: TurnPayload?,
    inputMode: InputMode
): TurnEnvelopeV3 {
    val request = TurnCommandV3(
        requestId = buildRequestId(),
        event = event,
        payload = payload,
        clientContext = ClientContext(
            inputMode = inputMode,
            clientTs = js("new Date().toISOString()") as String,
            clientProtocol = ClientProtocolWire.value
        ),
        session = snapshot
    )
    val response = window.fetch(
        "/api/v1/web/session/$sessionId/turn",
        RequestInit(
            method = "POST",
            headers = jsonHeaders(),
            body = webJson.encodeToString(request)
        )
    ).await()
    return response.decodeOrThrow()
}

internal suspend inline fun <reified T> org.w3c.fetch.Response.decodeOrThrow(): T {
    val body = text().await()
    if (!ok) {
        val message =
            runCatching { webJson.decodeFromString<interviewprep.common.ApiError>(body).message }
                .getOrElse { body.take(240).ifBlank { "Request failed" } }
        throw IllegalStateException(message)
    }
    return webJson.decodeFromString(body)
}

internal fun buildRequestId(): String {
    val random = kotlin.random.Random.nextInt(100000, 999999)
    return "web-${js("Date.now()") as Double}-$random"
}

private fun jsonHeaders(): dynamic = js("{ 'Content-Type': 'application/json' }")
